import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useRecordContext, type RaRecord } from 'react-admin';
import { toErrorMessage } from '../userOffers/orderActionsApi';
import { fetchVehiclePlan, type VehiclePlan } from './vehiclePlanApi';
import type { GanttPreview } from './VehicleGantt';

/**
 * Wspólny stan karty pojazdu (07.10.2026, prototyp p. Mariusza).
 *
 * Sekcje karty mają własne zapytania, ale trzy rzeczy z prototypu łączą je
 * ze sobą:
 *
 * - **podsumowanie na górze i baner alertu** (zmiany 1 i 5) czytają plan
 *   auta w domyślnym oknie API (tydzień wstecz, trzy naprzód) — niezależnie
 *   od tego, gdzie planista przewinął oś harmonogramu, „bieżące” i „następne
 *   zlecenie” liczą się od teraz;
 * - **podgląd rekomendowanego zlecenia na osi** (zmiana 15) — wiersz tabeli
 *   rekomendacji mówi, co pokazać, harmonogram to rysuje.
 *
 * Bez dostawcy (np. sekcja testowana osobno) — pusty stan, a ustawienie
 * podglądu nic nie robi.
 */

export interface CarOverview {
  plan: VehiclePlan | null;
  error: string | null;
  loading: boolean;
}

interface CarCardState {
  overview: CarOverview;
  preview: GanttPreview | null;
  setPreview: (preview: GanttPreview | null) => void;
}

const EMPTY: CarCardState = {
  overview: { plan: null, error: null, loading: false },
  preview: null,
  setPreview: () => undefined,
};

const CarCardContext = createContext<CarCardState>(EMPTY);

export const useCarCard = (): CarCardState => useContext(CarCardContext);

export const CarCardProvider = ({ children }: { children: ReactNode }) => {
  const record = useRecordContext<RaRecord>();
  const id = record?.id;
  const [overview, setOverview] = useState<CarOverview>({ plan: null, error: null, loading: id !== undefined });
  const [preview, setPreview] = useState<GanttPreview | null>(null);

  useEffect(() => {
    if (id === undefined || id === null) return undefined;
    let cancelled = false;
    setOverview({ plan: null, error: null, loading: true });
    setPreview(null);
    fetchVehiclePlan(id)
      .then((plan) => {
        if (!cancelled) setOverview({ plan, error: null, loading: false });
      })
      .catch((e) => {
        if (!cancelled) setOverview({ plan: null, error: toErrorMessage(e), loading: false });
      });
    return () => {
      cancelled = true;
    };
  }, [id]);

  const value = useMemo(() => ({ overview, preview, setPreview }), [overview, preview]);
  return <CarCardContext.Provider value={value}>{children}</CarCardContext.Provider>;
};

/**
 * Sekcja „Rekomendowane zlecenia” jako cel przycisku z banera alertu —
 * jak `vehicleSuggestionsAnchor.ts` na karcie zlecenia: stałe `id` sekcji
 * i znacznik przycisku, dopóki rekomendacje nie są policzone.
 */
export const ORDER_SUGGESTIONS_ANCHOR_ID = 'order-suggestions';
export const ORDER_SUGGEST_START_SELECTOR = 'button[data-suggest-start]';

/** Przewija do rekomendacji i liczy je, jeśli jeszcze nie są policzone. */
export function showOrderSuggestions(): boolean {
  const section = document.getElementById(ORDER_SUGGESTIONS_ANCHOR_ID);
  if (!section) return false;
  section.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  const start = section.querySelector<HTMLButtonElement>(ORDER_SUGGEST_START_SELECTOR);
  if (start && !start.disabled) start.click();
  return true;
}
